import { useState } from 'react';
import { Download, FileText, CheckCircle, AlertCircle, Search, Eye } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useABAFiles, useDownloadABA, useValidateABA } from '@/hooks/useABA';
import { ABAFile } from '@/types';
import { formatCurrency, formatDate } from '@/lib/utils';

export function ABAFilesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [previewFile, setPreviewFile] = useState<ABAFile | null>(null);
  
  const { toast } = useToast();
  const { data: filesData, isLoading } = useABAFiles({
    page: currentPage,
    limit: pageSize,
  });
  
  const downloadABA = useDownloadABA();
  const validateABA = useValidateABA();

  const files = filesData?.data || [];
  const totalItems = filesData?.meta?.total || 0;

  const handleDownload = async (id: string, fileName: string) => {
    try {
      const blob = await downloadABA.mutateAsync(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Success',
        description: 'ABA file downloaded successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to download ABA file',
        variant: 'destructive',
      });
    }
  };

  const handleValidate = async (id: string) => {
    try {
      const result = await validateABA.mutateAsync(id);
      if (result.valid) {
        toast({
          title: 'Validation Passed',
          description: 'ABA file is valid',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Validation Failed',
          description: result.errors.join(', '),
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to validate ABA file',
        variant: 'destructive',
      });
    }
  };

  const columns: Column<ABAFile>[] = [
    {
      key: 'fileName',
      header: 'File Name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.fileName}</span>
        </div>
      ),
    },
    {
      key: 'payRunNumber',
      header: 'Pay Run',
      sortable: true,
      cell: (row) => row.payRunNumber,
    },
    {
      key: 'paymentDate',
      header: 'Payment Date',
      sortable: true,
      cell: (row) => formatDate(row.paymentDate),
    },
    {
      key: 'totalAmount',
      header: 'Total Amount',
      sortable: true,
      cell: (row) => <span className="font-bold">{formatCurrency(row.totalAmount)}</span>,
    },
    {
      key: 'transactionCount',
      header: 'Transactions',
      sortable: true,
      cell: (row) => row.transactionCount,
    },
    {
      key: 'validation',
      header: 'Status',
      sortable: true,
      cell: (row) => (
        <Badge variant={row.isValid ? 'success' : 'destructive'} className="flex items-center w-fit">
          {row.isValid ? (
            <>
              <CheckCircle className="h-3 w-3 mr-1" />
              Valid
            </>
          ) : (
            <>
              <AlertCircle className="h-3 w-3 mr-1" />
              Invalid
            </>
          )}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      cell: (row) => (
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setPreviewFile(row)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => handleDownload(row.id, row.fileName)}
            disabled={downloadABA.isPending}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Calculate summary stats
  const totalAmount = files.reduce((sum, f) => sum + f.totalAmount, 0);
  const validFiles = files.filter(f => f.isValid).length;
  const invalidFiles = files.filter(f => !f.isValid).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="ABA Files"
        description="Generate and manage ABA files for bank payments"
      />

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Total Files</p>
          <p className="text-2xl font-bold">{totalItems}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Total Value</p>
          <p className="text-2xl font-bold">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Valid Files</p>
          <p className="text-2xl font-bold text-success">{validFiles}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Invalid Files</p>
          <p className="text-2xl font-bold text-destructive">{invalidFiles}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search ABA files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={files}
        columns={columns}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        emptyMessage="No ABA files found. Generate ABA files from approved pay runs to get started."
      />

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-card z-10">
              <CardTitle>ABA File Preview: {previewFile.fileName}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setPreviewFile(null)}>
                Close
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Pay Run:</span>
                    <span className="ml-2 font-medium">{previewFile.payRunNumber}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Payment Date:</span>
                    <span className="ml-2 font-medium">{formatDate(previewFile.paymentDate)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Amount:</span>
                    <span className="ml-2 font-medium">{formatCurrency(previewFile.totalAmount)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Transactions:</span>
                    <span className="ml-2 font-medium">{previewFile.transactionCount}</span>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4 bg-muted/50">
                  <h4 className="font-medium mb-2">File Content Preview</h4>
                  <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {previewFile.fileContent.substring(0, 2000)}
                    {previewFile.fileContent.length > 2000 && '...'}
                  </pre>
                </div>

                {previewFile.validationErrors && previewFile.validationErrors.length > 0 && (
                  <div className="border border-destructive rounded-lg p-4 bg-destructive/10">
                    <h4 className="font-medium text-destructive mb-2">Validation Errors</h4>
                    <ul className="list-disc list-inside text-sm text-destructive">
                      {previewFile.validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => handleValidate(previewFile.id)}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Validate
                  </Button>
                  <Button onClick={() => handleDownload(previewFile.id, previewFile.fileName)}>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
